import dynamic from 'next/dynamic';
import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import useCommunityCookieProperties from '@aurora/shared-client/components/community/useCommunityCookieProperties';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import Icons from '@aurora/shared-client/icons';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React, { useContext, useEffect, useState } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { useUIDSeed } from 'react-uid';
import useTranslation from '../../useTranslation';
import localStyles from './CookieBanner.module.pcss';
import type { GdprCookies } from '@aurora/shared-client/helpers/cookie/CookieHelper';
import {
  fetchGdprCookies,
  GdprCookiePreferences,
  GdprCookieValues,
  LITHIUM_NECESSARY,
  setGdprCookies
} from '@aurora/shared-client/helpers/cookie/CookieHelper';

const CookiePreferencesModal = dynamic(
  () => import('./CookiePreferencesModal/CookiePreferencesModal'),
  { ssr: false }
);

/**
 * Cookie Banner
 *
 * @author Rishabh Jivan
 */
const CookieBanner: React.FC<React.PropsWithChildren<unknown>> = () => {
  const cx = useClassNameMapper(localStyles);
  const { data, loading } = useCommunityCookieProperties(module);
  const {
    formatMessage,
    FormattedMessage,
    loading: textLoading
  } = useTranslation(EndUserComponent.COOKIE_BANNER);
  const {
    publicConfig: { trafficVisitorCookieMaxAge }
  } = useContext(TenantContext);
  const gdprCookies: GdprCookies = fetchGdprCookies();
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const uidSeed = useUIDSeed();
  const ariaTitleId = uidSeed('alertTitle');
  const ariaBodyId = uidSeed('alertBody');

  useEffect(() => {
    if (!loading) {
      const { isCookieBannerEnabled } = data?.community?.cookieProperties ?? {};
      if (isCookieBannerEnabled) {
        if (!gdprCookies.userPreferenceExist) {
          setGdprCookies(gdprCookies.preferences);
        }
        setShowBanner(!gdprCookies.userPreferenceExist);
      }
    }
  }, [data, gdprCookies, loading]);

  if (textLoading || !showBanner || loading) {
    return null;
  }

  const { cookiePrivacyPolicyUrl } = data?.community?.cookieProperties ?? {};

  /**
   * Handler for when the Accept button is clicked.
   * This will set all the cookie categories to accepted.
   */
  const onAccept = () => {
    const cookiePrefs: GdprCookiePreferences = new GdprCookiePreferences();
    for (const key of Object.keys(gdprCookies.preferences)) {
      cookiePrefs[key] = GdprCookieValues.ACCEPTED;
    }
    setGdprCookies(cookiePrefs, trafficVisitorCookieMaxAge);
    setShowBanner(false);
  };

  /**
   * Handler for when the Decline button is clicked.
   * This will set only the necessary cookies to accepted and the rest are rejected.
   */
  const onDecline = () => {
    const cookiePrefs: GdprCookiePreferences = new GdprCookiePreferences();
    for (const key of Object.keys(gdprCookies.preferences)) {
      cookiePrefs[key] = GdprCookieValues.REJECTED;
    }
    cookiePrefs.LithiumNecessaryCookiesAccepted = GdprCookieValues.ACCEPTED;
    setGdprCookies(cookiePrefs, trafficVisitorCookieMaxAge);
    setShowBanner(false);
  };

  /**
   * Function that is called when the submit button is clicked from the preferences modal
   *
   * @param result Object containing the user's selected cookie preferences
   */
  const onPreferenceSubmitted = result => {
    const cookiePrefs: GdprCookiePreferences = new GdprCookiePreferences();
    for (const key of Object.keys(gdprCookies.preferences)) {
      if (key === LITHIUM_NECESSARY || result.preferences[key]) {
        cookiePrefs[key] = GdprCookieValues.ACCEPTED;
      } else {
        cookiePrefs[key] = GdprCookieValues.REJECTED;
      }
    }
    setGdprCookies(cookiePrefs, trafficVisitorCookieMaxAge);
    setShowBanner(false);
  };

  return (
    <>
      <div
        className={cx('lia-banner')}
        role="alertdialog"
        aria-labelledby={ariaTitleId}
        aria-describedby={ariaBodyId}
        data-testid="CookieBanner"
      >
        <span className={cx('lia-title')} id={ariaTitleId}>
          {formatMessage('bannerTitle')}
        </span>
        <span className={cx('lia-message')} id={ariaBodyId}>
          <FormattedMessage
            id="bannerText"
            values={{
              Link: function renderChunks(chunks): React.ReactNode {
                return cookiePrivacyPolicyUrl ? (
                  <a href={cookiePrivacyPolicyUrl} target="_blank" rel="noreferrer">
                    {chunks}
                    <Icon
                      icon={Icons.ExternalLinkIcon}
                      className={cx('lia-link-icon')}
                      size={IconSize.PX_14}
                      color={IconColor.LINK_COLOR}
                    />
                  </a>
                ) : (
                  <>{chunks}</>
                );
              }
            }}
          />
        </span>
        <div className={cx('lia-actions')}>
          <Button
            variant={ButtonVariant.SECONDARY}
            onClick={() => setShowModal(true)}
            data-testid="CookieBanner.PreferencesButton"
          >
            {formatMessage('preferences')}
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            onClick={onDecline}
            data-testid="CookieBanner.DeclineButton"
          >
            {formatMessage('decline')}
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            onClick={onAccept}
            data-testid="CookieBanner.AcceptButton"
          >
            {formatMessage('accept')}
          </Button>
        </div>
      </div>
      {showModal && (
        <CookiePreferencesModal
          show={true}
          onHide={() => setShowModal(false)}
          onSubmitted={onPreferenceSubmitted}
          cookiePrivacyPolicyUrl={cookiePrivacyPolicyUrl}
        />
      )}
    </>
  );
};

export default CookieBanner;
